<template>
  <div class="app-header-global-search">
    <!-- No trigger keys: Element Plus would otherwise take Space, Enter and Down typed in the search box to open and
         close the results -->
    <el-dropdown
      v-if="searchActive"
      ref="dropdown"
      :trigger-keys="[]"
      popper-class="app-header-global-search__popper"
      trigger="click"
    >
      <!-- Search input -->
      <el-input
        ref="globalInput"
        :model-value="searchQuery"
        autofocus
        placeholder="e.g. #188, “fix browser bug”"
        class="app-header-global-search__input"
        @input="change"
        @keydown.down.prevent="focusFirstResult"
        @keydown.esc="closeResults"
      >
        <template #suffix>
          <i class="ddi ddi-close el-icon-closeDD el-input__icon" @click="closeSearch" />
        </template>
        <template #prefix>
          <i class="ddi ddi-search el-icon-search el-input__icon" />
        </template>
      </el-input>

      <!-- Results -->
      <template #dropdown>
        <el-dropdown-menu
          ref="dropdownRef"
          :style="[setStyleDropdown ? {margin:0, height:0, padding:0} : { }]"
          class="app-header-global-search__dropdown"
          @focus="keepFocusInSearch"
        >
          <el-dropdown-item
            v-if="hasMailboxOrProject"
            v-show="!globalSearchIsLoading"
            class="app-header-global-search__dropdown-title no-hover"
          >Mailboxes and Projects</el-dropdown-item>

          <!-- MAILBOXES -->
          <el-dropdown-item
            v-for="item in globalSearchMailboxes"
            v-show="!globalSearchIsLoading"
            :key="'Mailbox'+item.id"
            :command="item"
            class="app-header-global-search__dropdown__simple"
            @click="sendToHome(EEntities.MAILBOXES, item.id, item.name)"
          >
            <div class="title" v-html="item.name" />
            <div class="app-header-global-search__dropdown-team">Mailbox</div>
          </el-dropdown-item>

          <!-- PROJECTS -->
          <el-dropdown-item
            v-for="item in globalSearchProjects"
            v-show="!globalSearchIsLoading"
            :key="'Project'+item.id"
            :command="item"
            class="app-header-global-search__dropdown__simple"
            @click="sendToHome(EEntities.PROJECTS, item.id, item.name)"
          >
            <div class="title" v-html="item.name" />
            <div class="app-header-global-search__dropdown-team">Project</div>
          </el-dropdown-item>

          <el-dropdown-item
            v-if="hasMailboxOrProject && hasSearchMatches"
            v-show="!globalSearchIsLoading"
            class="app-header-global-search__dropdown-divider no-hover"
            divided
          />

          <!-- Results with avatar -->
          <el-dropdown-item
            v-if="hasSearchMatches"
            v-show="!globalSearchIsLoading"
            class="app-header-global-search__dropdown-title no-hover"
          >Conversations and Tasks</el-dropdown-item>
          <el-dropdown-item
            v-for="(item, index) in globalSearchMatches"
            v-show="!globalSearchIsLoading"
            :key="'ConvTask'+index"
            :command="item"
            class="el-dropdown-item__avatar"
            @click="sendToComment(item.url)"
          >
            <dd-global-search-avatar-row :item="item" />
          </el-dropdown-item>

          <!-- NO RESULTS -->
          <dd-global-search-empty :text="emptySearchText" />

          <!-- LOADING FROM API -->
          <dd-global-search-loading
            :search-active="searchActive"
            :is-loading="globalSearchIsLoading"
          />
          <dd-global-search-loading
            :search-active="searchActive"
            :is-loading="globalSearchIsLoading"
          />
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { debounce } from 'lodash-es'

/* Mixins */

/* Components */
import DdGlobalSearchEmpty from './components/app-header-global-search-empty.vue'
import DdGlobalSearchAvatarRow from './components/app-header-global-search-avatar-row.vue'
import DdGlobalSearchLoading from './components/app-header-global-search-loading.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { EEntities } from '@/types'
import {
  ISearchItemResult,
  ISearchOutputIndex,
  IItemForSelection,
} from '@/api/types/response.types'

/* API */
import SearchAPI from '@/api/modules/search.api'
import { DropdownInstance } from 'element-plus'

export default defineComponent({
  name: 'AppHeaderGlobalSearch',

  components: {
    DdGlobalSearchEmpty,
    DdGlobalSearchAvatarRow,
    DdGlobalSearchLoading,
  },

  props: {
    searchActive: {
      type: Boolean,
      required: true,
    },
  },

  emits: ['toggle-search', 'visible'],

  data() {
    return {
      EEntities,
      searchQuery: '',
      debounceGetResults: null as any,
      errorText: '',

      globalSearchIsLoading: false,
      globalSearchResults: {} as ISearchOutputIndex,
    }
  },

  computed: {
    globalSearchMailboxes(): IItemForSelection[] {
      return this.globalSearchResults.mailboxes
    },

    globalSearchProjects(): IItemForSelection[] {
      return this.globalSearchResults.projects
    },

    globalSearchMatches(): ISearchItemResult[] {
      return this.globalSearchResults.matches
    },

    setStyleDropdown(): boolean {
      return (
        (this.getGlobalSearchLength === 0 &&
          !this.globalSearchIsLoading &&
          !this.showNoResults) ||
        this.searchQuery.length < 2
      )
    },

    hasMailboxOrProject(): boolean {
      let length = 0
      if (this.globalSearchMailboxes) {
        length += this.globalSearchMailboxes.length
      }
      if (this.globalSearchProjects) {
        length += this.globalSearchProjects.length
      }
      return length > 0
    },

    hasSearchMatches(): boolean {
      return this.globalSearchMatches && this.globalSearchMatches.length > 0
    },

    getGlobalSearchLength(): number {
      let length = 0
      if (this.globalSearchResults !== null) {
        if (this.globalSearchResults.mailboxes) {
          length += this.globalSearchResults.mailboxes.length
        }
        if (this.globalSearchResults.projects) {
          length += this.globalSearchResults.projects.length
        }
        if (this.globalSearchResults.matches) {
          length += this.globalSearchResults.matches.length
        }
      }
      return length
    },

    showNoResults(): boolean {
      return (
        this.searchActive &&
        this.getGlobalSearchLength === 0 &&
        this.searchQuery.length > 2 &&
        !this.globalSearchIsLoading
      )
    },

    emptySearchText(): string {
      if (this.errorText !== '') {
        return this.errorText
      }
      if (this.showNoResults) {
        return 'No results found'
      }
      return null
    },
  },

  watch: {
    async searchQuery() {
      if (this.searchQuery.length >= 2) {
        await this.debounceGetResults()
        if (this.$refs.dropdown) {
          ;(this.$refs.dropdown as DropdownInstance).handleOpen()
        }
      }
    },
    searchActive(val: boolean) {
      if (val === true) {
        this.$nextTick(() => {
          // @ts-ignore
          this.$refs.globalInput.focus()
          if (this.searchQuery.length >= 2 && this.$refs.dropdown) {
            ;(this.$refs.dropdown as DropdownInstance).handleOpen()
          }
        })
      }
    },
  },

  mounted() {
    this.getSearchItemResults()
  },

  created() {
    this.debounceGetResults = debounce(this.getSearchItemResults, 1000)
  },

  methods: {
    closeSearch() {
      this.searchQuery = ''
      this.$emit('toggle-search', false)
    },

    async getSearchItemResults() {
      this.errorText = ''
      if (this.searchQuery === '') {
        return
      }

      this.globalSearchResults = {} as ISearchOutputIndex
      this.globalSearchIsLoading = true

      try {
        this.globalSearchResults = await SearchAPI.post_Index(this.searchQuery)
      } catch (err) {
        // @ts-ignore
        this.errorText = err.data.message
      } finally {
        this.globalSearchIsLoading = false
      }
    },

    sendToHome(type: string, entityID: any, title: string) {
      if (type === EEntities.MAILBOXES) {
        RouteAssistant.goToMailbox(
          this.$router,
          this.$route.params.accountId,
          entityID,
          title,
        )
      } else {
        RouteAssistant.goToProjectLanding(
          this.$router,
          this.$route.params.accountId,
          entityID,
          this.$permissions,
          title,
        )
      }
    },
    /**
     * Send to comment/reply URL
     */
    sendToComment(url: string) {
      window.location.href = url
    },

    /**
     * Update v-model as @input is triggered.
     */
    change(val: any) {
      this.searchQuery = val
    },

    /**
     * Element Plus focuses the results as they open, which would leave the rest of the search typed into them
     */
    keepFocusInSearch() {
      // @ts-ignore
      this.$refs.globalInput.focus()
    },

    /**
     * Down moves from the search box into the results, as it did with Element UI
     */
    focusFirstResult() {
      const firstResult = document.querySelector(
        '.app-header-global-search__popper .el-dropdown-menu__item:not(.no-hover)',
      ) as HTMLElement | null
      firstResult?.focus()
    },

    closeResults() {
      ;(this.$refs.dropdown as DropdownInstance | undefined)?.handleClose()
    },
  },
})
</script>

<style lang="scss">
.el-dropdown-menu.app-header-global-search__dropdown {
  background-color: var(--color-background-header);
}

.app-header-global-search {
  width: 100%;

  .el-dropdown {
    display: flex;
    align-items: center;
    width: 100%;
  }

  &__input {
    flex-grow: 1;
    width: 100%;
    line-height: normal;

    @include media-breakpoint-up(md) {
      width: 274px;
      max-width: 100%;
    }

    .el-input__inner {
      &:focus {
        line-height: 1.44;
        color: $slate !important;
      }

      &:-ms-input-placeholder {
        line-height: 1.44;
        color: $slate !important;
      }
    }

    .el-icon-search {
      position: relative;
      font-size: 16px;
      color: var(--color-text-third);
      opacity: 1;
    }

    .el-icon-closeDD {
      position: relative;
      font-size: 12px;
      color: var(--color-text-third);
      opacity: 1;
    }
  }

  &__dropdown {
    overflow-x: hidden;
    overflow-y: auto;
    cursor: pointer;

    &-title {
      margin: 2px 0 6px 8px;
      color: var(--color-text-primary) !important;
      @include lettering--h5;
    }

    &-divider {
      margin-bottom: 8px;
    }

    &-team {
      @include lettering--p-sm;
      color: var(--color-text-third);
    }

    &__simple {
      margin: 12px 8px;
      border-radius: 8px;

      .title {
        margin-top: 9px;
        margin-bottom: -2px;
        @include lettering--p;
        font-weight: $bold;
      }
    }

    .el-dropdown-item,
    .el-dropdown-menu__item.el-dropdown-item {
      width: 100%;
      margin-right: 8px;
      margin-left: 8px;
    }
  }

}

/* The dropdown's popper, which is what gets positioned; the extra class outranks the popper rules in _dropdown.scss */
.el-dropdown__popper.el-popper.app-header-global-search__popper {
  position: absolute;
  top: $navbar-mobile-height - 6 !important;
  width: 100%;
  height: calc(100% - 52px);
  max-height: 672px;
  /* The results scroll in here, as they did in Element UI's menu, which was the popper */
  overflow-x: hidden;
  overflow-y: auto;
  background-color: var(--color-background-header);
  border-radius: 3px;

  @include media-breakpoint-down(md) {
    top: $navbar-mobile-height - 6px !important;
    right: 100px !important;
    left: 0 !important;
    max-width: 558px;
  }

  @include media-breakpoint-up(md) {
    right: 72px !important;
    left: auto !important;
    max-width: 584px;
    height: 100%;
    max-height: 672px;
  }

  @media (min-width: $--md) {
    top: 60px !important;
    height: auto;
  }

  .el-dropdown-menu__item {
    &:focus {
      border-radius: 8px;
    }

    &:hover {
      border-radius: 8px;
    }
  }

  .el-popper__arrow {
    display: none;
  }
}
</style>
